import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import { itemService, type Item } from '../services';
import { companyConfigService, type CompanyConfig } from '../services/companyConfigService';
import { comboService, type Combo } from '../services/comboService';
import logo from '../assets/logo.jpeg';
import iconInstagram from '../assets/iconInstagram.jpeg';
import iconFacebook from '../assets/iconFacebook.jpeg';
import iconPix from '../assets/iconPix.png';
import iconVisa from '../assets/iconVisa.png';
import iconMastercard from '../assets/iconMastercard.png';
import iconDinheiro from '../assets/iconDinheiro.png';
import iconWhatsapp from '../assets/iconWhatsapp.jpeg';

interface MarketingMediaDialogProps {
  open: boolean;
  onClose: () => void;
  items: Item[];
  companyConfig: CompanyConfig | null;
  combos: Combo[];
  showPrices: boolean;
  variant?: 'menu' | 'cta';
}

const MarketingMediaDialog: React.FC<MarketingMediaDialogProps> = ({
  open,
  onClose,
  items,
  companyConfig,
  combos,
  showPrices,
  variant = 'menu',
}) => {
  const telefoneMensagem = companyConfig?.message || '';

  const mediaElementId =
    variant === 'cta'
      ? 'marketing-media-cta'
      : showPrices
      ? 'marketing-media-with-prices'
      : 'marketing-media-without-prices';

  const handleExportJpeg = async () => {
    if (typeof window === 'undefined') return;

    const mediaElement = document.getElementById(mediaElementId);

    if (!mediaElement) return;

    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(mediaElement, {
        backgroundColor: '#111',
        scale: 2,
      });
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);

      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = showPrices ? 'midia-betterdog-precos.jpg' : 'midia-betterdog-sem-preco.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      // silencioso: se der erro, o usuário ainda pode usar Exportar / Imprimir
      console.error('Erro ao exportar JPEG da mídia:', error);
    }
  };

  const handleExport = () => {
    if (typeof window === 'undefined') return;

    const mediaElement = document.getElementById(
      showPrices ? 'marketing-media-with-prices' : 'marketing-media-without-prices'
    );

    if (!mediaElement) {
      return;
    }

    const printWindow = window.open('', '_blank', 'width=1024,height=768');
    if (!printWindow) return;

    const styles = `
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @page { size: 14cm 20cm; margin: 0; }
        html, body {
          margin: 0;
          padding: 0;
          width: 100%;
          height: 100%;
          background: #111;
          font-family: Roboto, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        .media-root {
          width: 14cm;
          height: 20cm;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .media-root-inner {
          width: 100%;
          height: 100%;
        }
        img { max-width: 100%; height: auto; display: block; }
      </style>
    `;

    printWindow.document.open();
    printWindow.document.write(`<!doctype html><html><head><meta charset="utf-8" />${styles}</head><body>`);
    printWindow.document.write(`<div class="media-root"><div class="media-root-inner">${mediaElement.innerHTML}</div></div>`);
    printWindow.document.write('</body></html>');
    printWindow.document.close();

    const handlePrintAndClose = () => {
      try {
        printWindow.print();
      } finally {
        printWindow.close();
      }
    };

    if (printWindow.document.readyState === 'complete') {
      handlePrintAndClose();
    } else {
      printWindow.onload = handlePrintAndClose;
    }
  };

  const renderFooter = () => (
    <>
      <Box
        sx={{
          mt: 3,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        {(() => {
          const tradicionalDuplo = items.find(
            (item) =>
              item.available &&
              item.category === 'tradicionais' &&
              item.imageUrl &&
              item.name.toLowerCase().includes('duplo')
          );

          const combo1 = combos.find(
            (combo) =>
              combo.ativo &&
              combo.imageUrl &&
              combo.nome.toLowerCase().includes('combo 1')
          );

          const combo4 = combos.find(
            (combo) =>
              combo.ativo &&
              combo.imageUrl &&
              combo.nome.toLowerCase().includes('combo 4')
          );

          const fallbacks: Array<Item | Combo> = [];
          if (!tradicionalDuplo) {
            const anyTradicional = items.find(
              (item) =>
                item.available &&
                item.category === 'tradicionais' &&
                item.imageUrl
            );
            if (anyTradicional) fallbacks.push(anyTradicional);
          }
          if (!combo1 || !combo4) {
            const anyComboImages = combos.filter(
              (combo) => combo.ativo && combo.imageUrl
            );
            fallbacks.push(...anyComboImages);
          }

          const ordered = [tradicionalDuplo, combo1, combo4, ...fallbacks].filter(
            (v, idx, arr) => v && arr.indexOf(v) === idx
          ) as Array<Item | Combo>;

          return ordered.slice(0, 3).map((entry) => (
            <Box
              key={('id' in entry ? entry.id : '') + (entry.imageUrl || '')}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: 4,
                bgcolor: '#000',
                maxWidth: 220,
              }}
            >
              <Box
                component="img"
                src={entry.imageUrl}
                alt={'nome' in entry ? entry.nome : 'Item'}
                sx={{
                  width: '100%',
                  height: 150,
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            </Box>
          ));
        })()}
      </Box>

      <Typography
        variant="caption"
        sx={{
          mt: 1.5,
          display: 'block',
          textAlign: 'center',
          color: '#bbb',
          fontSize: '0.9rem',
        }}
      >
        Imagens meramente ilustrativas.
      </Typography>
    </>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {variant === 'cta'
              ? 'Mídia chamada para pedido'
              : showPrices
              ? 'Mídia com preços'
              : 'Mídia sem preços'}
          </Typography>
          {telefoneMensagem && (
            <Typography variant="subtitle2" color="text.secondary">
              {telefoneMensagem}
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: '#e0e0e0' }}>
        <Box display="flex" justifyContent="center">
          <Box
            id={mediaElementId}
            sx={{
              bgcolor: '#111',
              color: '#fff',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: 6,
              maxWidth: 920,
              width: '100%',
            }}
          >
            {variant === 'cta' ? (
              <Box
                sx={{
                  px: { xs: 2, sm: 4 },
                  py: 4,
                  bgcolor: '#111',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  minHeight: 700,
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Box
                    component="img"
                    src={logo}
                    alt="Better Dog logo"
                    sx={{
                      width: 150,
                      height: 150,
                      borderRadius: '30%',
                      objectFit: 'cover',
                      boxShadow: 4,
                      border: '3px solid #FFB300',
                      mb: 3,
                    }}
                  />

                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 900,
                      letterSpacing: 1,
                      mb: 1,
                      fontStyle: 'italic',
                      textTransform: 'none',
                      color: '#FFB300',
                    }}
                  >
                    Delivery
                  </Typography>

                  <Box
                    mt={1.5}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                    }}
                  >
                    <Box
                      component="img"
                      src={iconWhatsapp}
                      alt="WhatsApp"
                      sx={{ width: 50, height: 50, borderRadius: '50%' }}
                    />
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 800 }}
                    >
                      (51) 99219-8889
                    </Typography>
                  </Box>

                  <Typography
                    variant="subtitle1"
                    sx={{ mt: 1.5, fontWeight: 600 }}
                  >
                    FAÇA JÁ O SEU PEDIDO!
                  </Typography>

                  <Box mt={3} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box
                          component="img"
                          src={iconInstagram}
                          alt="Instagram"
                          sx={{ width: 22, height: 22, borderRadius: '50%' }}
                        />
                        <Typography variant="body2" sx={{ color: '#fff' }}>
                          @betterdogdelivery
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box
                          component="img"
                          src={iconFacebook}
                          alt="Facebook"
                          sx={{ width: 22, height: 22, borderRadius: '50%' }}
                        />
                        <Typography variant="body2" sx={{ color: '#fff' }}>
                          Better DogDelivery
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ color: '#ddd' }}>
                        Atendimento: 18h às 23h
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="body2" sx={{ color: '#ddd' }}>
                        Meios de pagamento
                      </Typography>
                      <Box
                        sx={{
                          bgcolor: '#fff',
                          borderRadius: 1,
                          px: 1.5,
                          py: 0.75,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                        }}
                      >
                        <Box
                          component="img"
                          src={iconPix}
                          alt="Pix"
                          sx={{ width: 40, height: 24, objectFit: 'contain' }}
                        />
                        <Box
                          component="img"
                          src={iconVisa}
                          alt="Visa"
                          sx={{ width: 46, height: 28, objectFit: 'contain' }}
                        />
                        <Box
                          component="img"
                          src={iconMastercard}
                          alt="Mastercard"
                          sx={{ width: 46, height: 28, objectFit: 'contain' }}
                        />
                        <Box
                          component="img"
                          src={iconDinheiro}
                          alt="Dinheiro"
                          sx={{ width: 40, height: 26, objectFit: 'contain' }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {renderFooter()}
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    px: { xs: 2, sm: 4 },
                    py: 3,
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    bgcolor: 'linear-gradient(90deg, #000000 0%, #212121 60%, #FF6F00 100%)',
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box
                      component="img"
                      src={logo}
                      alt="Better Dog logo"
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: '30%',
                        objectFit: 'cover',
                        boxShadow: 3,
                        border: '2px solid #FFB300',
                      }}
                    />
                    <Box>
                      <Typography
                        variant="h5"
                        fontWeight={900}
                        sx={{ letterSpacing: 1, fontSize: '2rem' }}
                      >
                        BETTER DOG
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{ color: '#FFB300', mt: 0.25, fontSize: '1.15rem' }}
                      >
                        Cardápio Promocional
                      </Typography>
                    </Box>
                  </Box>
                  <Box mt={{ xs: 2, md: 0 }} textAlign={{ xs: 'left', md: 'right' }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, letterSpacing: 0.5, fontSize: '1.35rem' }}
                    >
                      Peça já seu delivery
                    </Typography>
                    {telefoneMensagem && (
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        color="#FFB300"
                        sx={{ fontSize: '1.4rem' }}
                      >
                        {telefoneMensagem}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    px: { xs: 2, sm: 4 },
                    py: 3,
                    bgcolor: '#111',
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      columnGap: { xs: 3, md: 6 },
                      rowGap: 3,
                    }}
                  >
                    <Box>
                      {[
                        { key: 'tradicionais', label: 'Tradicionais' },
                        { key: 'especiais', label: 'Especiais' },
                        { key: 'acompanhamentos', label: 'Acompanhamentos' },
                      ].map((section) => {
                        let sectionItems = items.filter(
                          (item) => item.available && item.category === section.key
                        );

                        if (section.key === 'tradicionais') {
                          const order = ['duplo', 'triplo', 'linguiça', 'linguica'];
                          sectionItems = sectionItems.slice().sort((a, b) => {
                            const aIndex = order.findIndex((token) =>
                              a.name.toLowerCase().includes(token)
                            );
                            const bIndex = order.findIndex((token) =>
                              b.name.toLowerCase().includes(token)
                            );

                            const aRank = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
                            const bRank = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;

                            if (aRank !== bRank) return aRank - bRank;
                            return a.name.localeCompare(b.name, 'pt-BR');
                          });
                        }

                        if (sectionItems.length === 0) return null;

                        return (
                          <Box key={section.key} mb={2.5}>
                            <Typography
                              variant="h6"
                              sx={{
                                color: '#FFB300',
                                fontWeight: 700,
                                letterSpacing: 1,
                                mb: 1,
                                textTransform: 'uppercase',
                                fontSize: '1.3rem',
                              }}
                            >
                              {section.label}
                            </Typography>

                            {sectionItems.map((item) => (
                              <Box
                                key={item.id}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  justifyContent: 'flex-start',
                                  mb: 1,
                                  gap: 1,
                                }}
                              >
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ color: '#fff', fontWeight: 600, fontSize: '1.05rem' }}
                                  >
                                    {item.name}
                                    {item.newItem && (
                                      <Chip
                                        label="Novo"
                                        color="secondary"
                                        size="small"
                                        sx={{ ml: 1, fontSize: '0.65rem' }}
                                      />
                                    )}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#ddd',
                                      fontSize: '0.95rem',
                                      maxWidth: 260,
                                    }}
                                  >
                                    {item.description}
                                  </Typography>
                                </Box>

                                {showPrices && (
                                  <Box
                                    sx={{
                                      bgcolor: '#FFB300',
                                      borderRadius: 999,
                                      px: 1.5,
                                      py: 0.25,
                                      minWidth: 70,
                                      textAlign: 'center',
                                      ml: 1.5,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ fontWeight: 700, color: '#000', fontSize: '1rem' }}
                                    >
                                      {item.price.toLocaleString('pt-BR', {
                                        style: 'currency',
                                        currency: 'BRL',
                                      })}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            ))}
                          </Box>
                        );
                      })}
                    </Box>

                    <Box>
                      {(() => {
                        const section = { key: 'bebidas', label: 'Bebidas' };
                        const sectionItems = items.filter(
                          (item) => item.available && item.category === section.key
                        );

                        return sectionItems.length > 0 ? (
                          <Box mb={2.5}>
                            <Typography
                              variant="h6"
                              sx={{
                                color: '#FFB300',
                                fontWeight: 700,
                                letterSpacing: 1,
                                mb: 1,
                                textTransform: 'uppercase',
                                fontSize: '1.3rem',
                              }}
                            >
                              {section.label}
                            </Typography>

                            {sectionItems.map((item) => (
                              <Box
                                key={item.id}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  justifyContent: 'space-between',
                                  mb: 1,
                                  gap: 1,
                                }}
                              >
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ color: '#fff', fontWeight: 600, fontSize: '1.05rem' }}
                                  >
                                    {item.name}
                                    {item.newItem && (
                                      <Chip
                                        label="Novo"
                                        color="secondary"
                                        size="small"
                                        sx={{ ml: 1, fontSize: '0.65rem' }}
                                      />
                                    )}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#ddd',
                                      fontSize: '0.95rem',
                                      maxWidth: 260,
                                    }}
                                  >
                                    {item.description}
                                  </Typography>
                                </Box>

                                {showPrices && (
                                  <Box
                                    sx={{
                                      bgcolor: '#FFB300',
                                      borderRadius: 999,
                                      px: 1.5,
                                      py: 0.25,
                                      minWidth: 70,
                                      textAlign: 'center',
                                      ml: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ fontWeight: 700, color: '#000', fontSize: '1rem' }}
                                    >
                                      {item.price.toLocaleString('pt-BR', {
                                        style: 'currency',
                                        currency: 'BRL',
                                      })}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            ))}
                          </Box>
                        ) : null;
                      })()}

                      {combos.filter((combo) => combo.ativo).length > 0 && (
                        <Box mt={3}>
                          <Typography
                            variant="h6"
                            sx={{
                              color: '#FFB300',
                              fontWeight: 700,
                              letterSpacing: 1,
                              mb: 1,
                              textTransform: 'uppercase',
                              fontSize: '1.3rem',
                            }}
                          >
                            Combos
                          </Typography>

                          {combos
                            .filter((combo) => combo.ativo)
                            .slice()
                            .sort((a, b) => {
                              const extractNumber = (name: string) => {
                                const match = name.match(/(\d+)/);
                                return match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
                              };

                              const numA = extractNumber(a.nome.toLowerCase());
                              const numB = extractNumber(b.nome.toLowerCase());

                              if (numA !== numB) return numA - numB;
                              return a.nome.localeCompare(b.nome, 'pt-BR');
                            })
                            .map((combo) => (
                              <Box
                                key={combo.id}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  justifyContent: 'flex-start',
                                  mb: 1,
                                  gap: 1,
                                }}
                              >
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ color: '#fff', fontWeight: 600, fontSize: '1.05rem' }}
                                  >
                                    {combo.nome}
                                  </Typography>
                                  {combo.descricao && (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: '#ddd',
                                        fontSize: '0.95rem',
                                        maxWidth: 260,
                                      }}
                                    >
                                      {combo.descricao}
                                    </Typography>
                                  )}
                                </Box>

                                {showPrices && (
                                  <Box
                                    sx={{
                                      bgcolor: '#FFB300',
                                      borderRadius: 999,
                                      px: 1.5,
                                      py: 0.25,
                                      minWidth: 70,
                                      textAlign: 'center',
                                      ml: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ fontWeight: 700, color: '#000', fontSize: '1rem' }}
                                    >
                                      {(combo.precoPromocional ?? combo.precoBase).toLocaleString('pt-BR', {
                                        style: 'currency',
                                        currency: 'BRL',
                                      })}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            ))}
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {renderFooter()}
                </Box>
              </>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleExportJpeg} color="secondary" variant="outlined">
          Exportar JPEG
        </Button>
        <Button onClick={handleExport} color="primary" variant="contained">
          Exportar / Imprimir
        </Button>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

const MediaManagement: React.FC = () => {
  const [items, setItems] = useState<Item[]>([]);
  const [companyConfig, setCompanyConfig] = useState<CompanyConfig | null>(null);
  const [combos, setCombos] = useState<Combo[]>([]);
  const [loading, setLoading] = useState(true);
  const [openWithPrices, setOpenWithPrices] = useState(false);
  const [openWithoutPrices, setOpenWithoutPrices] = useState(false);
  const [openCta, setOpenCta] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [itemsResult, companyResult, combosResult] = await Promise.all([
        itemService.listItems(),
        companyConfigService.getMyConfig(),
        comboService.listCombos(undefined, true),
      ]);
      setItems(itemsResult);
      setCompanyConfig(companyResult);
      setCombos(combosResult);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <Container maxWidth="lg" sx={{ py: 2, px: { xs: 1, sm: 2 } }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Mídias de Marketing
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gere artes automáticas do seu cardápio para usar em divulgações e campanhas.
        </Typography>
      </Box>

      <Paper elevation={2} sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
          }}
        >
          <Box>
            <Typography variant="h6" gutterBottom>
              Gerar mídias
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use as mídias abaixo para compartilhar em redes sociais, status ou impressões.
            </Typography>
          </Box>

          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
            <Button
              variant="contained"
              color="primary"
              disabled={loading || items.length === 0}
              onClick={() => setOpenWithPrices(true)}
            >
              Mídia com preços
            </Button>
            <Button
              variant="outlined"
              color="primary"
              disabled={loading || items.length === 0}
              onClick={() => setOpenWithoutPrices(true)}
            >
              Mídia sem preços
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              disabled={loading || items.length === 0}
              onClick={() => setOpenCta(true)}
            >
              Mídia chamada para pedido
            </Button>
          </Box>
        </Box>

        {!loading && items.length === 0 && (
          <Box mt={3}>
            <Typography variant="body2" color="text.secondary">
              Nenhum item disponível. Cadastre itens em "Itens" para gerar suas mídias.
            </Typography>
          </Box>
        )}
      </Paper>

      <MarketingMediaDialog
        open={openWithPrices}
        onClose={() => setOpenWithPrices(false)}
        items={items}
        companyConfig={companyConfig}
        combos={combos}
        showPrices
      />

      <MarketingMediaDialog
        open={openWithoutPrices}
        onClose={() => setOpenWithoutPrices(false)}
        items={items}
        companyConfig={companyConfig}
        combos={combos}
        showPrices={false}
      />

      <MarketingMediaDialog
        open={openCta}
        onClose={() => setOpenCta(false)}
        items={items}
        companyConfig={companyConfig}
        combos={combos}
        showPrices
        variant="cta"
      />
    </Container>
  );
};

export default MediaManagement;
